import { FilterInput } from '@apeiron/common'
import { NextImage, noForwardProps } from '@apeiron/temp'
import { Box, SliderProps, styled, Typography } from '@mui/material'
import RangeSliderWithInput from '@src/components/share/filter/RangeSliderWithInput'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const unitScale = (value: number): number => value

const Label = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 6px;
`

const LabelIcon = styled(NextImage)`
  aspect-ratio: 1;
  width: 16px;
`

const LabelText = styled(Box, noForwardProps)<LabelTextProps>`
  color: ${props => (props.light ? 'white' : '#333333')};
`

const LabelRemark = styled(Typography)`
  color: #80807e;
  display: inline;
  font-size: 14px;
  margin-left: 5px;
`

const RangeSlider = <T extends FilterInput>(props: Props<T>) => {
  const {
    className,
    decimalScale,
    filterOptions,
    labelIconURL,
    labelText,
    max: sliderMax = 0,
    maxValueKey,
    min: sliderMin = 0,
    minValueKey,
    remark,
    descale = unitScale,
    scale = unitScale,
    onFilterOptionsChange,
    ...rest
  } = props

  const lastFilterOptions = useRef<T>(filterOptions)

  const conditionalDescale = useCallback(
    (key: string, fallback: number) => {
      const value = filterOptions[key as keyof FilterInput]

      return !R.isNil(value) ? descale(value) : fallback
    },
    [descale, filterOptions]
  )

  const [sliderMinValue, setSliderMinValue] = useState<number>(
    conditionalDescale(minValueKey, sliderMin)
  )

  const [sliderMaxValue, setSliderMaxValue] = useState<number>(
    conditionalDescale(maxValueKey, sliderMax)
  )

  const label = useMemo(
    () => (
      <Label>
        {RA.isNotNilOrEmpty(labelIconURL) && <LabelIcon src={labelIconURL} />}
        {RA.isNotNilOrEmpty(remark) ? (
          <LabelText>
            {labelText}
            <LabelRemark>{remark}</LabelRemark>
          </LabelText>
        ) : (
          <LabelText>{labelText}</LabelText>
        )}
      </Label>
    ),
    [labelIconURL, labelText, remark]
  )

  const onValueChange = useCallback(
    (sliderNewMin: number, sliderNewMax: number) => {
      setSliderMinValue(sliderNewMin)
      setSliderMaxValue(sliderNewMax)
      lastFilterOptions.current = {
        ...filterOptions,
        [minValueKey]:
          !R.isNil(sliderNewMin) && sliderNewMin !== sliderMin
            ? scale(sliderNewMin)
            : undefined,
        [maxValueKey]:
          !R.isNil(sliderNewMax) && sliderNewMax !== sliderMax
            ? scale(sliderNewMax)
            : undefined
      }
    },
    [filterOptions, maxValueKey, minValueKey, sliderMax, sliderMin, scale]
  )

  const handleOnChangeCommitted = useCallback(() => {
    onFilterOptionsChange(lastFilterOptions.current)
  }, [onFilterOptionsChange])

  useEffect(() => {
    const presetSliderMin = conditionalDescale(minValueKey, sliderMin)

    if (sliderMinValue !== presetSliderMin) {
      setSliderMinValue(presetSliderMin)
    }

    const presetSliderMax = conditionalDescale(maxValueKey, sliderMax)

    if (sliderMaxValue !== presetSliderMax) {
      setSliderMaxValue(presetSliderMax)
    }
  }, [filterOptions])

  return (
    <RangeSliderWithInput
      className={className}
      decimalScale={decimalScale}
      label={label}
      max={sliderMax}
      maxValue={sliderMaxValue}
      min={sliderMin}
      minValue={sliderMinValue}
      descale={descale}
      scale={scale}
      onValueChange={onValueChange}
      onChangeCommitted={handleOnChangeCommitted}
      {...rest}
    />
  )
}

type LabelTextProps = {
  light?: boolean
}

type Props<T> = Omit<SliderProps, 'onChangeCommitted'> & {
  className?: string
  decimalScale?: number
  filterOptions: T
  labelIconURL?: string
  labelText: string
  maxValueKey: string
  minValueKey: string
  remark?: string
  descale?: (value: number) => number
  onFilterOptionsChange: (filterOptions: T) => void
}

export default RangeSlider
